import { TIMING, easeDoor, floorAtHeight, travelTime } from "./timing.js";
import type { Timing } from "./timing.js";

/** What a cabin is doing. */
export type CabinPhase = "idle" | "opening" | "open" | "closing" | "moving";
export type Dir = -1 | 0 | 1;
export type SimEvent = "arrive" | "depart" | "pass" | "call" | "reopen" | "open" | "close";

export interface Cabin {
  index: number;
  phase: CabinPhase;
  /** Height of the cabin floor (m, building local frame). */
  y: number;
  /** The floor (index) the cabin is at; while moving, the floor it passed last. */
  at: number;
  /** The floor it rides to while moving. */
  to: number;
  /** Direction of service: the arrow of the hall indicator. */
  dir: Dir;
  /** 0 closed .. 1 open (linear in time; draw it through `easeDoor`). */
  door: number;
  /** Floor buttons lit inside. */
  car: boolean[];
  /** Seconds left of the dwell with the doors open. */
  dwell: number;
  /** Seconds since leaving (moving). */
  t: number;
  /** Speed along the ride (m/s, unsigned; the direction is `dir`). */
  v: number;
  /** A person or thing in the doorway (set by the caller before each step). */
  blocked: boolean;
  /** The door-open button is held. */
  hold: boolean;
  /** How often the safety reopened the doors. */
  reopens: number;
}

/** Seconds a stop costs on the way (doors opening, closing, a short wait). */
const STOP_COST = 2 * TIMING.door + 0.5;

/**
 * The elevator brain of one building: `n` floors served by one or two cabins, hall calls with a
 * direction, floor buttons inside, collective control (a cabin stops for every call on its way in
 * its direction) and a dispatch that gives each new hall call to the cabin that gets there first.
 * Deterministic: only `step(dt)` moves time. Nothing is allocated after construction.
 */
export class ElevatorSim {
  readonly cabins: Cabin[];
  readonly up: boolean[];
  readonly down: boolean[];
  /** Cabin that answers each hall call (-1 none). */
  readonly upBy: number[];
  readonly downBy: number[];
  onEvent: ((e: SimEvent, cabin: number, floor: number) => void) | null = null;
  /** Simulated seconds so far. */
  time = 0;

  constructor(
    readonly ys: readonly number[],
    cabinCount: number,
    readonly timing: Timing = TIMING,
    start: readonly number[] = [],
  ) {
    const n = ys.length;
    const flags = () => Array.from({ length: n }, () => false);
    this.up = flags();
    this.down = flags();
    this.upBy = Array.from({ length: n }, () => -1);
    this.downBy = Array.from({ length: n }, () => -1);
    this.cabins = Array.from({ length: cabinCount }, (_, i) => {
      // The first cabin waits in the lobby, the second at the top.
      const at = Math.max(0, Math.min(n - 1, start[i] ?? (i === 0 ? 0 : n - 1)));
      return {
        index: i,
        phase: "idle",
        y: ys[at] ?? 0,
        at,
        to: at,
        dir: 0,
        door: 0,
        car: flags(),
        dwell: 0,
        t: 0,
        v: 0,
        blocked: false,
        hold: false,
        reopens: 0,
      };
    });
  }

  get floors(): number {
    return this.ys.length;
  }

  private emit(e: SimEvent, c: Cabin, floor: number): void {
    this.onEvent?.(e, c.index, floor);
  }

  // ------------------------------------------------------------------ buttons

  /** Presses a hall button. Returns the cabin that will come (-1 when there is no such button). */
  callHall(floor: number, dir: 1 | -1): number {
    if (floor < 0 || floor >= this.floors) return -1;
    if ((dir === 1 && floor === this.floors - 1) || (dir === -1 && floor === 0)) return -1;
    // A cabin standing here answers at once: doors open, or reopen, or keep waiting.
    for (const c of this.cabins) {
      if (c.at !== floor || c.phase === "moving") continue;
      if (c.phase === "open" || c.phase === "opening") c.dwell = this.timing.dwell;
      else if (c.phase === "closing") this.reopen(c);
      else this.open(c, dir);
      return c.index;
    }
    const lit = dir === 1 ? this.up : this.down;
    const by = dir === 1 ? this.upBy : this.downBy;
    if (!lit[floor]) {
      lit[floor] = true;
      this.emit("call", this.cabins[0]!, floor);
    }
    if (by[floor]! < 0) by[floor] = this.pick(floor, dir);
    return by[floor]!;
  }

  /** Presses a floor button inside a cabin. */
  pressCar(cabin: number, floor: number): boolean {
    const c = this.cabins[cabin];
    if (!c || floor < 0 || floor >= this.floors) return false;
    if (floor === c.at && c.phase !== "moving") {
      if (c.phase === "idle") this.open(c, c.dir);
      else if (c.phase === "closing") this.reopen(c);
      else c.dwell = this.timing.dwell;
      return true;
    }
    if (!c.car[floor]) {
      c.car[floor] = true;
      this.emit("call", c, floor);
    }
    // Someone has chosen: the doors do not wait the whole dwell.
    if (c.phase === "open") c.dwell = Math.min(c.dwell, this.timing.grace);
    return true;
  }

  /** The door buttons: open (reopen, or wait longer) or close now. */
  pressDoor(cabin: number, what: "open" | "close"): void {
    const c = this.cabins[cabin];
    if (!c || c.phase === "moving") return;
    if (what === "open") {
      if (c.phase === "closing") this.reopen(c);
      else if (c.phase === "idle") this.open(c, c.dir);
      else c.dwell = this.timing.dwell;
    } else if (c.phase === "open" && !c.blocked) {
      c.dwell = 0;
    }
  }

  // ------------------------------------------------------------------ dispatch

  /**
   * Seconds a cabin needs to get to `floor` for a call going `dir`: straight there when the call
   * is on its way, else to the end of its run, turning round and back.
   */
  eta(c: Cabin, floor: number, dir: Dir): number {
    const tm = this.timing;
    const here = c.phase === "moving" ? c.y : this.ys[c.at]!;
    const ride = (a: number, b: number) => travelTime(b - a, tm);
    let wait = 0;
    if (c.phase === "open" || c.phase === "opening") wait = tm.dwell;
    else if (c.phase === "closing") wait = tm.door;
    const stops = (a: number, b: number) => this.stopsBetween(c, a, b) * STOP_COST;
    const d: 1 | -1 = c.dir === 0 ? (floor >= c.at ? 1 : -1) : c.dir;
    const onTheWay = (floor - c.at) * d >= 0;
    const far = this.farthest(c, d);
    if (c.dir === 0 || (onTheWay && (dir === d || far === floor || dir === 0))) {
      return wait + ride(here, this.ys[floor]!) + stops(c.at, floor);
    }
    return (
      wait +
      ride(here, this.ys[far]!) +
      ride(this.ys[far]!, this.ys[floor]!) +
      stops(c.at, far) +
      stops(far, floor) +
      STOP_COST
    );
  }

  private pick(floor: number, dir: Dir): number {
    let best = 0;
    let bestEta = Infinity;
    for (const c of this.cabins) {
      const e = this.eta(c, floor, dir);
      if (e < bestEta - 1e-9) {
        bestEta = e;
        best = c.index; // ties keep the lower index: deterministic
      }
    }
    return best;
  }

  private stopsBetween(c: Cabin, a: number, b: number): number {
    const lo = Math.min(a, b);
    const hi = Math.max(a, b);
    let n = 0;
    for (let f = lo + 1; f < hi; f++) if (this.wants(c, f)) n++;
    return n;
  }

  /** Does anything call this cabin to `f`: a button inside or a hall call assigned to it. */
  private wants(c: Cabin, f: number): boolean {
    return (
      c.car[f]! ||
      (this.up[f]! && this.upBy[f] === c.index) ||
      (this.down[f]! && this.downBy[f] === c.index)
    );
  }

  /** The last floor the cabin has to serve in direction `d` (its own floor when none). */
  private farthest(c: Cabin, d: 1 | -1): number {
    let far = c.at;
    for (let f = 0; f < this.floors; f++) {
      if (f !== c.at && this.wants(c, f) && (d === 1 ? f > far : f < far)) far = f;
    }
    return far;
  }

  private beyond(c: Cabin, d: 1 | -1): boolean {
    return this.farthest(c, d) !== c.at;
  }

  /** Calls at the floor the cabin stands at: 0 none, 1 up, -1 down, 2 inside or both. */
  private hereCall(c: Cabin): number {
    const f = c.at;
    if (c.car[f]) return 2;
    const u = this.up[f]! && this.upBy[f] === c.index;
    const d = this.down[f]! && this.downBy[f] === c.index;
    if (u && d) return 2;
    return u ? 1 : d ? -1 : 0;
  }

  /**
   * The next floor to ride to, with collective control: the nearest call ahead in the direction
   * of service (an opposite-direction hall call counts only as the last stop of the run), else
   * the nearest behind. -1 when nothing waits.
   */
  private nextStop(c: Cabin): number {
    const from = c.at;
    if (c.dir !== 0) {
      for (const d of [c.dir, c.dir === 1 ? -1 : 1] as const) {
        const far = this.farthest(c, d);
        for (let f = from + d; f >= 0 && f < this.floors; f += d) {
          if (!this.wants(c, f)) continue;
          const opposite =
            d === 1
              ? this.down[f]! && this.downBy[f] === c.index
              : this.up[f]! && this.upBy[f] === c.index;
          const same =
            d === 1
              ? this.up[f]! && this.upBy[f] === c.index
              : this.down[f]! && this.downBy[f] === c.index;
          if (opposite && !same && !c.car[f] && f !== far) continue; // picked up on the way back
          return f;
        }
      }
      return -1;
    }
    let best = -1;
    let dist = Infinity;
    for (let f = 0; f < this.floors; f++) {
      if (f === from || !this.wants(c, f)) continue;
      const d = Math.abs(this.ys[f]! - this.ys[from]!);
      if (d < dist - 1e-9) {
        dist = d;
        best = f;
      }
    }
    return best;
  }

  /** Puts out the lights of the calls the cabin answers here; returns the hall direction served. */
  private clearHere(c: Cabin): Dir {
    const f = c.at;
    c.car[f] = false;
    let served: Dir = 0;
    // Against the direction of service a hall call is answered only at the end of the run.
    const upOk = c.dir !== -1 || !this.beyond(c, -1);
    const downOk = c.dir !== 1 || !this.beyond(c, 1);
    if (this.up[f] && this.upBy[f] === c.index && upOk) {
      this.up[f] = false;
      this.upBy[f] = -1;
      served = 1;
    }
    if (this.down[f] && this.downBy[f] === c.index && downOk) {
      this.down[f] = false;
      this.downBy[f] = -1;
      served = served === 1 ? 0 : -1;
    }
    return served;
  }

  // ------------------------------------------------------------------ the clock

  private open(c: Cabin, dir: Dir): void {
    c.phase = "opening";
    if (dir !== 0) c.dir = dir;
    this.clearHere(c);
    this.emit("open", c, c.at);
  }

  private reopen(c: Cabin): void {
    c.phase = "opening";
    c.reopens++;
    this.emit("reopen", c, c.at);
  }

  /** Advances the simulation by `dt` seconds (in steps of at most 1/30 s). */
  step(dtRaw: number): void {
    let left = dtRaw;
    while (left > 1e-9) {
      const dt = Math.min(left, 1 / 30);
      left -= dt;
      this.time += dt;
      for (const c of this.cabins) this.stepCabin(c, dt);
    }
  }

  private stepCabin(c: Cabin, dt: number): void {
    const tm = this.timing;
    switch (c.phase) {
      case "idle": {
        const here = this.hereCall(c);
        if (here !== 0) {
          this.open(c, here === 2 ? c.dir : (here as Dir));
          return;
        }
        const next = this.nextStop(c);
        if (next < 0) return;
        c.to = next;
        c.dir = next > c.at ? 1 : -1;
        c.t = 0;
        c.v = 0;
        c.phase = "moving";
        this.emit("depart", c, c.at);
        return;
      }
      case "opening": {
        c.door = Math.min(1, c.door + dt / tm.door);
        if (c.door >= 1) {
          c.phase = "open";
          c.dwell = tm.dwell;
        }
        return;
      }
      case "open": {
        if (c.blocked || c.hold) {
          c.dwell = Math.max(c.dwell, tm.grace);
          return;
        }
        // Another call here keeps the doors a while longer.
        if (this.hereCall(c) !== 0) {
          this.clearHere(c);
          c.dwell = tm.dwell;
        }
        c.dwell -= dt;
        if (c.dwell <= 0) c.phase = "closing";
        return;
      }
      case "closing": {
        // Safety: a person in the doorway, the open button, or a new call here reopens the doors.
        if (c.blocked || c.hold || this.hereCall(c) !== 0) {
          this.clearHere(c);
          this.reopen(c);
          return;
        }
        c.door = Math.max(0, c.door - dt / tm.door);
        if (c.door <= 0) {
          c.phase = "idle";
          this.emit("close", c, c.at);
        }
        return;
      }
      case "moving": {
        c.t += dt;
        this.retarget(c);
        const sign = c.dir === -1 ? -1 : 1;
        const rem = (this.ys[c.to]! - c.y) * sign;
        // Brake so as to stop exactly at the target, within the acceleration limit.
        const want = Math.min(tm.speed, Math.sqrt(2 * tm.accel * Math.max(rem, 0)));
        const dv = want - c.v;
        const lim = tm.accel * dt;
        c.v += dv > lim ? lim : dv < -lim ? -lim : dv;
        const step = c.v * dt;
        if (rem <= 1e-4 || step >= rem) {
          this.arrive(c);
          return;
        }
        c.y += sign * step;
        const f = floorAtHeight(this.ys, c.y, c.at);
        if (f !== c.at) {
          c.at = f;
          if (f !== c.to) this.emit("pass", c, f);
        }
        return;
      }
    }
  }

  /**
   * Collective control on the road: a call at a floor that can still be braked for (and that lies
   * before the current target) becomes the new target.
   */
  private retarget(c: Cabin): void {
    const d = c.dir === -1 ? -1 : 1;
    const brake = (c.v * c.v) / (2 * this.timing.accel) + 0.15;
    const far = this.farthest(c, d);
    for (let f = d === 1 ? 0 : this.floors - 1; f >= 0 && f < this.floors; f += d) {
      const ahead = (this.ys[f]! - c.y) * d;
      if (ahead < brake) continue;
      if ((this.ys[c.to]! - this.ys[f]!) * d <= 1e-6) break; // not before the target
      if (!this.wants(c, f)) continue;
      const opposite =
        d === 1
          ? this.down[f]! && this.downBy[f] === c.index
          : this.up[f]! && this.upBy[f] === c.index;
      const same =
        d === 1
          ? this.up[f]! && this.upBy[f] === c.index
          : this.down[f]! && this.downBy[f] === c.index;
      if (opposite && !same && !c.car[f] && f !== far) continue;
      c.to = f;
      break;
    }
  }

  private arrive(c: Cabin): void {
    c.y = this.ys[c.to]!;
    c.at = c.to;
    c.v = 0;
    c.phase = "opening";
    const heading = c.dir;
    const served = this.clearHere(c);
    // The arrow follows what is left to do; a served hall call shows where that person goes.
    if (heading !== 0 && this.beyond(c, heading)) c.dir = heading;
    else if (served !== 0) c.dir = served;
    else if (heading !== 0 && this.beyond(c, heading === 1 ? -1 : 1))
      c.dir = heading === 1 ? -1 : 1;
    else c.dir = 0;
    this.emit("arrive", c, c.at);
  }

  /**
   * Puts a cabin at a floor with its doors open (the player boards from the floor list). Calls
   * it held are given back to the dispatcher.
   */
  dockAt(cabin: number, floor: number): void {
    const c = this.cabins[cabin];
    if (!c || floor < 0 || floor >= this.floors) return;
    c.phase = "open";
    c.door = 1;
    c.dwell = this.timing.grace * 2;
    c.at = floor;
    c.to = floor;
    c.y = this.ys[floor]!;
    c.v = 0;
    c.dir = 0;
    c.t = 0;
    c.car.fill(false);
    for (let f = 0; f < this.floors; f++) {
      if (this.upBy[f] === cabin) this.upBy[f] = this.up[f] ? this.pick(f, 1) : -1;
      if (this.downBy[f] === cabin) this.downBy[f] = this.down[f] ? this.pick(f, -1) : -1;
    }
  }

  /** The door opening (0..1) as drawn: the eased leaf position. */
  doorAt(cabin: number): number {
    return easeDoor(this.cabins[cabin]!.door);
  }
}
